import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  X, ChevronRight, ChevronLeft, Check, Building, Users, 
  Wallet, Calendar, Settings, Sparkles, ArrowRight
} from 'lucide-react';

const ONBOARDING_KEY = 'syndicappli_onboarding_completed';

const OnboardingWizard = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const steps = [
    {
      id: 'welcome',
      icon: Sparkles,
      title: `Bienvenue ${user?.prenom || ''} !`,
      subtitle: "Découvrez SyndicAppli en quelques étapes",
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            SyndicAppli vous accompagne dans la gestion de vos copropriétés. 
            Ce guide rapide vous aidera à prendre en main les fonctionnalités essentielles.
          </p>
          <div className="bg-[#00D3A8]/5 border border-[#00D3A8]/20 rounded-xl p-4">
            <p className="text-sm text-[#00917a] font-medium">
              Temps estimé : 2 minutes
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'immeubles',
      icon: Building,
      title: "1. Créez vos immeubles",
      subtitle: "La base de votre gestion",
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            Chaque immeuble représente une copropriété distincte. Commencez par ajouter 
            vos immeubles avec leurs informations essentielles.
          </p>
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#00D3A8] rounded-full flex items-center justify-center flex-shrink-0">
                <Check size={14} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Adresse et nom de l'immeuble</p>
                <p className="text-xs text-slate-500">Identifiez clairement chaque copropriété</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#00D3A8] rounded-full flex items-center justify-center flex-shrink-0">
                <Check size={14} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Nombre de lots et tantièmes</p>
                <p className="text-xs text-slate-500">Pour la répartition des charges</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#00D3A8] rounded-full flex items-center justify-center flex-shrink-0">
                <Check size={14} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Documents importants</p>
                <p className="text-xs text-slate-500">Règlement, diagnostics, plans...</p>
              </div>
            </div>
          </div>
        </div>
      ),
      action: () => navigate('/immeubles'),
      actionLabel: "Accéder aux immeubles"
    },
    {
      id: 'coproprietaires',
      icon: Users,
      title: "2. Ajoutez les coproprietaires",
      subtitle: "Vos interlocuteurs au quotidien",
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            Enregistrez les coproprietaires avec leurs lots et tantièmes. 
            Ils pourront accéder à leur espace personnel pour consulter leurs documents.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
              <p className="text-xs font-medium text-blue-700 mb-1">Import CSV</p>
              <p className="text-xs text-blue-600">Importez plusieurs coproprietaires en une fois</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 border border-green-100">
              <p className="text-xs font-medium text-green-700 mb-1">Invitation email</p>
              <p className="text-xs text-green-600">Envoyez les accès automatiquement</p>
            </div>
          </div>
        </div>
      ),
      action: () => navigate('/coproprietaires'),
      actionLabel: "Gérer les coproprietaires"
    },
    {
      id: 'comptabilite',
      icon: Wallet,
      title: "3. Configurez la comptabilité",
      subtitle: "Conforme au décret 2005-240",
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            La comptabilité de copropriété suit des règles strictes. 
            Notre guide pas à pas vous accompagne dans la mise en place.
          </p>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Étapes clés :</p>
            <ol className="text-xs text-slate-600 space-y-1.5">
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 bg-[#00D3A8] rounded-full text-white flex items-center justify-center text-[10px] font-bold">1</span>
                Créer l'exercice comptable
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 bg-[#00D3A8] rounded-full text-white flex items-center justify-center text-[10px] font-bold">2</span>
                Définir le budget prévisionnel
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 bg-[#00D3A8] rounded-full text-white flex items-center justify-center text-[10px] font-bold">3</span>
                Émettre les appels de fonds
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 bg-[#00D3A8] rounded-full text-white flex items-center justify-center text-[10px] font-bold">4</span>
                Enregistrer les factures
              </li>
            </ol>
          </div>
        </div>
      ),
      action: () => navigate('/comptabilite'),
      actionLabel: "Accéder à la comptabilité"
    },
    {
      id: 'ag',
      icon: Calendar,
      title: "4. Préparez vos AG",
      subtitle: "Assemblées Générales simplifiées",
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            Organisez vos Assemblées Générales : convocations, ordre du jour, 
            votes et procès-verbaux, tout est centralisé.
          </p>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
            <p className="text-xs text-amber-700">
              <strong>Rappel légal :</strong> L'AG doit être convoquée au moins 21 jours 
              avant la date prévue (article 9 du décret du 17 mars 1967).
            </p>
          </div>
        </div>
      ),
      action: () => navigate('/assemblees'),
      actionLabel: "Voir les AG"
    },
    {
      id: 'complete',
      icon: Check,
      title: "Vous êtes prêt !",
      subtitle: "Commencez à gérer vos copropriétés",
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            Vous connaissez maintenant les bases de SyndicAppli. 
            Explorez les autres fonctionnalités à votre rythme.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button 
              onClick={() => navigate('/messagerie')}
              className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors text-left"
            >
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Settings size={16} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Messagerie</p>
                <p className="text-xs text-slate-500">Communiquez</p>
              </div>
            </button>
            <button 
              onClick={() => navigate('/incidents')}
              className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors text-left"
            >
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <Settings size={16} className="text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Incidents</p>
                <p className="text-xs text-slate-500">Suivez les problèmes</p>
              </div>
            </button>
          </div>
          <div className="pt-2">
            <button
              onClick={() => window.open('/aide', '_blank')}
              className="text-sm text-[#00D3A8] hover:underline flex items-center gap-1"
            >
              Accéder au centre d'aide
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
    if (onSkip) onSkip();
  };

  if (!isVisible) return null;

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" data-testid="onboarding-wizard">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#00D3A8] to-[#00B892] p-6 relative">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon className="text-white" size={28} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{currentStepData.title}</h2>
              <p className="text-white/80 text-sm">{currentStepData.subtitle}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="flex gap-1.5 mt-6">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full transition-all ${
                  index <= currentStep ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between">
          <div>
            {!isFirstStep && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
              >
                <ChevronLeft size={16} />
                Précédent
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!isLastStep && (
              <button
                onClick={handleSkip}
                className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                Passer
              </button>
            )}
            
            <button
              onClick={isLastStep ? handleComplete : handleNext}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#00D3A8] text-white rounded-xl text-sm font-medium hover:bg-[#00B892] transition-colors"
            >
              {isLastStep ? "Commencer" : "Suivant"}
              {!isLastStep && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to check if onboarding should be shown
export const useOnboarding = () => {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    setShouldShow(!completed);
  }, []);

  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_KEY);
    setShouldShow(true);
  };

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setShouldShow(false);
  };

  return { shouldShow, resetOnboarding, completeOnboarding };
};

export default OnboardingWizard;
